import { CameraStream } from "./camera_stream"

// Tasker preference for how "Take Photo" captures are sourced: the in-app
// live-camera flow (CameraStream) or a handoff to the phone's own camera app
// via a hidden file input (`capture="environment"`). See
// zenith/features/field-app/camera-native-handoff-opt-in-plan.md for the full
// design -- the short version is a device-detected default (iOS/Safari and
// any browser lacking real ImageCapture support default to native; everyone
// else defaults to in-app), with an explicit Settings override in either
// direction.
//
// Storage convention mirrors field_geolocation_controller.js's DENIED_KEY and
// field_battery_controller.js's BATTERY_MONITOR_KEY -- one exported key,
// plain localStorage reads/writes, no framework.
export const CAMERA_CAPTURE_MODE_KEY = "field_camera_capture_mode"

// The tasker's effective mode. Reads the explicit stored override; if none
// exists yet, seeds storage with recommendedCameraCaptureMode() and returns
// that -- so the detected value only ever seeds the FIRST read. Once a
// tasker has an explicit preference (seeded or chosen in Settings), later
// reads never get silently overwritten by a changed recommendation.
export function getCameraCaptureMode() {
  const stored = localStorage.getItem(CAMERA_CAPTURE_MODE_KEY)
  if (stored === "in-app" || stored === "native") return stored

  const recommended = recommendedCameraCaptureMode()
  try {
    localStorage.setItem(CAMERA_CAPTURE_MODE_KEY, recommended)
  } catch {
    // Storage unavailable (private mode, quota) -- fall through and still
    // return the recommendation for this read even though it won't persist.
  }
  return recommended
}

// Explicit tasker override, set from Settings.
export function setCameraCaptureMode(mode) {
  if (mode !== "in-app" && mode !== "native") return
  localStorage.setItem(CAMERA_CAPTURE_MODE_KEY, mode)
}

// Device-detected default, independent of any stored override -- exposed
// separately so Settings can always show "recommended for this device" even
// after a manual override diverges from it. True ImageCapture.takePhoto()
// support (mainly Android/Chrome) already gets near-native stills in-app, so
// native handoff would only add backgrounding risk for no proven upside --
// recommend staying in-app. Everywhere else (iOS Safari always, or any
// browser gap) is exactly the tier capped by frame-grab quality with no
// further software fix available -- recommend native.
export function recommendedCameraCaptureMode() {
  return new CameraStream().supportsStillCapture() ? "in-app" : "native"
}
